import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { getDataList, postDataEquiposlotes, postDataOt, POST, actualizarDatos, buscarPropuestas, crearDatos, resolverDependientesEquipos } from '../../../api/common';
import { useAuth } from '../../../hooks/useAuth';


/**
 * useCrearOt - Custom hook for managing OT creation state and handlers
 * @returns {Object} State and handlers for CrearOt component
 */
const useCrearOt = () => {
    const navigate = useNavigate();
    const { userData } = useAuth(); // Assuming you have an auth context to get user data

    // Main state
    const [cliente, setCliente] = useState(null);
    const [sede, setSede] = useState(null);
    const [equipos, setEquipos] = useState([]);
    const [numeroOrden, setNumeroOrden] = useState('');
    const [observacion, setObservacion] = useState('');
    const [propuestaComercial, setPropuestaComercial] = useState(null);

    console.log('Propuesta comercial seleccionada:', propuestaComercial);

    // Data lists
    const [listaSedes, setListaSedes] = useState([]);

    // Loading states
    const [loading, setLoading] = useState({
        sedes: false,
        validacion: false,
        creacion: false
    });

    // Modal states
    const [modalCrearCliente, setModalCrearCliente] = useState({ show: false, data: null });
    const [modalEditarSede, setModalEditarSede] = useState({ show: false, data: null });
    const [modalCrearSede, setModalCrearSede] = useState({ show: false });
    const [modalDuplicados, setModalDuplicados] = useState({ show: false, data: null });
    const [modalResolverItems, setModalResolverItems] = useState({ show: false, data: null });
    // Modal de confirmación de equipos dependientes (spec items-adicionales-equipo-dependiente).
    // Patrón Promise-based idéntico a modalDuplicados: el handler abre el modal y espera
    // que el usuario elija incluir todos / solo estos / cancelar.
    const [modalDependientes, setModalDependientes] = useState({
        show: false,
        grupos: [],
        onIncluirTodos: () => {},
        onSoloSeleccionados: () => {},
        onCancelar: () => {},
    });

    // Load sedes when cliente changes
    useEffect(() => {
        if (cliente) {
            loadSedes(cliente._id);
            // Clear sede and equipos when cliente changes
            setSede(null);
            setEquipos([]);
        } else {
            setListaSedes([]);
            setSede(null);
            setEquipos([]);
        }
    }, [cliente]);

    const loadSedes = async (clienteId) => {
        setLoading(prev => ({ ...prev, sedes: true }));
        try {
            const response = await getDataList('Sedes', { 
                conditions: { cliente: clienteId }
            }); 

            const sedes = response.data.data || [];
            console.log('🏢 loadSedes: Cliente ID:', clienteId, '| Sedes cargadas:', sedes);
            console.log('🏢 Sedes:', sedes.map(s => ({ id: s._id, nombre: s.nameSede })));
            setListaSedes(sedes);
            
            // Auto-select sede "Principal" if exists
            const sedePrincipal = sedes.find(sede => sede.nameSede === 'Principal');
            if (sedePrincipal) {
                setSede(sedePrincipal);
            } else if (sedes.length > 0) {
                // If no "Principal" sede, select the first one
                setSede(sedes[0]);
            }
        } catch (error) {
            console.error('Error loading sedes:', error);
            Swal.fire('Error', 'No se pudieron cargar las sedes del cliente', 'error');
        } finally {
            setLoading(prev => ({ ...prev, sedes: false }));
        }
    };

    const handleClienteChange = (nuevoCliente) => {
        setCliente(nuevoCliente);
    };

    const handleCrearCliente = () => {
        setModalCrearCliente({ show: true, data: null });
    };

    const handleSubmitCrearCliente = async (formData) => {
        try {
            const response = await POST('/usuarios/crear-cliente', formData);
            
            Swal.fire('Éxito', 'Cliente creado correctamente', 'success');
            
            // Auto-select new cliente
            setCliente(response.data.data);
            
            // Close modal
            setModalCrearCliente({ show: false, data: null });
        } catch (error) {
            console.error('Error creating cliente:', error);
            Swal.fire('Error', error.response?.data?.message || 'No se pudo crear el cliente', 'error');
        }
    };

    const handleSedeChange = (nuevaSede) => {
        setSede(nuevaSede);
    };

    const handleEditarSede = (sedeToEdit) => {
        setModalEditarSede({ show: true, data: sedeToEdit });
    };

    const handleCrearSede = () => {
        setModalCrearSede({ show: true });
    };

    const handleSubmitCrearSede = async (sedeData) => {
        try {
            const response = await crearDatos('Sedes', sedeData);
            const nuevaSede = response.data?.data || response.data;
            // Recargar lista y seleccionar la nueva sede
            const sedesRes = await getDataList('Sedes', { conditions: { cliente: cliente._id } });
            setListaSedes(sedesRes.data?.data || []);
            setSede(nuevaSede);
            setModalCrearSede({ show: false });
            Swal.fire('Éxito', 'Sede creada correctamente', 'success');
        } catch (error) {
            console.error('Error creating sede:', error);
            Swal.fire('Error', error.response?.data?.message || 'No se pudo crear la sede', 'error');
        }
    };

    const handleSubmitEditarSede = async (sedeData) => {
        try {
            const response = await actualizarDatos('Sedes', sedeData, sedeData._id);
            
            Swal.fire('Éxito', 'Sede actualizada correctamente', 'success');
            
            // Reload sedes list
            await loadSedes(cliente._id);
            
            // Update selected sede
            setSede(response.data.data);
            
            // Close modal
            setModalEditarSede({ show: false, data: null });
        } catch (error) {
            console.error('Error updating sede:', error);
            Swal.fire('Error', error.response?.data?.message || 'No se pudo actualizar la sede', 'error');
        }
    };

    const validarDuplicados = async (series) => {
        if (!cliente) return { cancelled: true, nuevosData: [], duplicadosData: [] };

        setLoading(prev => ({ ...prev, validacion: true }));
        
        try {
            const response = await POST('/Equipos/validar-duplicados', {
                series,
                clienteId: cliente._id
            });

            // Backend returns { nuevos: [], duplicados: [] } directly
            const { nuevos, duplicados } = response.data;

            if (duplicados.length > 0) {
                // Show duplicates modal and wait for user selection
                return new Promise((resolve) => {
                    setModalDuplicados({
                        show: true,
                        data: { nuevos, duplicados },
                        equiposYaAgregados: equipos, // Pass current equipos to prevent duplicates
                        onSelect: (selectedDuplicados) => {
                            setModalDuplicados({ show: false, data: null });
                            // Return structure with selected duplicados
                            resolve({ 
                                cancelled: false, 
                                nuevosData: nuevos, 
                                duplicadosData: selectedDuplicados 
                            });
                        },
                        onHide: () => {
                            setModalDuplicados({ show: false, data: null });
                            resolve({ cancelled: true, nuevosData: [], duplicadosData: [] });
                        }
                    });
                });
            }

            // No duplicates - all are nuevos
            return { cancelled: false, nuevosData: nuevos, duplicadosData: [] };
        } catch (error) {
            console.error('Error validating duplicates:', error);
            Swal.fire('Error', 'No se pudo validar duplicados', 'error');
            return { cancelled: true, nuevosData: [], duplicadosData: [] };
        } finally {
            setLoading(prev => ({ ...prev, validacion: false }));
        }
    };

    const handleResolverItems = (equiposSinItem, onResueltoCallback) => {
        setModalResolverItems({
            show: true,
            data: equiposSinItem,
            onResolve: (equiposResueltos) => {
                setModalResolverItems({ show: false, data: null });
                onResueltoCallback(equiposResueltos);
            },
            onHide: () => {
                setModalResolverItems({ show: false, data: null });
            }
        });
    };

    /**
     * Resuelve equipos dependientes antes de agregar/crear una OT.
     * Patrón Promise-based (mismo que validarDuplicados):
     *   - Consulta backend con los ids seleccionados.
     *   - Si no hay grupos → resuelve directo { cancelado:false, incluirTodos:true, extra:[], warning:null }.
     *   - Si hay grupos → abre modal y resuelve según el botón elegido.
     *     · Incluir todos: { cancelado:false, incluirTodos:true, extra:<todos los hijos+padres omitidos>, warning:null }.
     *     · Solo estos:   { cancelado:false, incluirTodos:false, extra:[], warning:<string ISO 17025> }.
     *     · Cancelar:     { cancelado:true }.
     */
    const handleResolverDependientes = async (equipoIds) => {
        if (!Array.isArray(equipoIds) || equipoIds.length === 0) {
            return { cancelado: false, incluirTodos: true, extra: [], warning: null };
        }
        try {
            const response = await resolverDependientesEquipos(equipoIds);
            const grupos = response.data?.grupos || [];

            if (grupos.length === 0) {
                return { cancelado: false, incluirTodos: true, extra: [], warning: null };
            }

            return new Promise((resolve) => {
                setModalDependientes({
                    show: true,
                    grupos,
                    onIncluirTodos: () => {
                        setModalDependientes(prev => ({ ...prev, show: false }));
                        // Reunir todos los padres no seleccionados + hijos no seleccionados para agregar.
                        const extra = [];
                        for (const g of grupos) {
                            if (!g.seleccionadoPadre) extra.push(g.padre);
                            for (const h of (g.hijosNoSeleccionados || [])) extra.push(h);
                        }
                        resolve({ cancelado: false, incluirTodos: true, extra, warning: null });
                    },
                    onSoloSeleccionados: (warningString) => {
                        setModalDependientes(prev => ({ ...prev, show: false }));
                        resolve({ cancelado: false, incluirTodos: false, extra: [], warning: warningString });
                    },
                    onCancelar: () => {
                        setModalDependientes(prev => ({ ...prev, show: false }));
                        resolve({ cancelado: true, incluirTodos: false, extra: [], warning: null });
                    },
                });
            });
        } catch (error) {
            console.error('Error resolviendo dependientes:', error);
            Swal.fire('Error', 'No se pudieron resolver los equipos dependientes. Intente nuevamente.', 'error');
            return { cancelado: true, incluirTodos: false, extra: [], warning: null };
        }
    };

    const agregarEquipos = (nuevosEquipos) => {
        console.log('🔵 agregarEquipos llamado con:', nuevosEquipos);
        console.log('🔵 Tipo:', Array.isArray(nuevosEquipos) ? 'Array' : typeof nuevosEquipos);
        
        // Normalize to array
        const equiposArray = Array.isArray(nuevosEquipos) ? nuevosEquipos : [nuevosEquipos];
        console.log('🔵 Equipos normalizados a array:', equiposArray.length, 'elementos');

        // Validate 500 equipos max
        if (equipos.length + equiposArray.length > 500) {
            Swal.fire(
                'Límite Excedido',
                `Se permiten máximo 500 equipos por OT. Actualmente tiene ${equipos.length} equipos. ` +
                `No se pueden agregar ${equiposArray.length} más.`,
                'warning'
            );
            return;
        }

        console.log('🟢 Agregando', equiposArray.length, 'equipos al estado actual:', equipos.length);
        setEquipos(prev => {
            const newArray = [...prev, ...equiposArray];
            console.log('🟢 Nuevo estado de equipos:', newArray.length, 'elementos');
            return newArray;
        });
    };

    const eliminarEquipo = (equipoId) => {
        setEquipos(prev => prev.filter(e => e._id !== equipoId));
    };

    const editarEquipo = (equipoId, datosActualizados) => {
        console.log('🔵 editarEquipo llamado con ID:', equipoId, 'datos:', datosActualizados);
        setEquipos(prev => prev.map(eq => 
            eq._id === equipoId ? { ...eq, ...datosActualizados } : eq
        ));
    };

    /**
     * corregirEquipo - Actualiza un equipo en BD y en el estado local
     * @param {string} equipoId - ID del equipo a corregir
     * @param {Object} datosActualizados - Datos actualizados del equipo
     * @returns {Promise<void>}
     */
    const corregirEquipo = async (equipoId, datosActualizados) => {
              
        try {
            // Actualizar en BD
            await actualizarDatos('Equipos', datosActualizados, equipoId);
            
            // Actualizar en estado local SOLO si el equipo ya existe en el array
            // (caso correctivo - equipo ya agregado a OT)
            const equipoExiste = equipos.some(eq => eq._id === equipoId);
            if (equipoExiste) {
                console.log('🔧 Actualizando equipo en estado local');
                setEquipos(prev => prev.map(eq => 
                    eq._id === equipoId ? { ...eq, ...datosActualizados } : eq
                ));
            } else {
                console.log('🔧 Equipo no está en lista actual (flujo preventivo), se agregará después');
            }
            
        } catch (error) {
            console.error('❌ Error corrigiendo equipo:', error);
            throw error; // Re-lanzar para que el modal lo maneje
        }
    };

    const handleCrearOt = async () => {
        // Validation
        if (!numeroOrden.trim()) {
            Swal.fire('Error', 'El número de orden es requerido', 'error');
            return;
        }

        if (!cliente || !sede) {
            Swal.fire('Error', 'Debe seleccionar un cliente y una sede', 'error');
            return;
        }

        if (equipos.length === 0) {
            Swal.fire('Error', 'Debe agregar al menos un equipo', 'error');
            return;
        }

        // Validar que todos los equipos tengan sede
        const equiposSinSede = equipos.filter(eq => !eq.sedeClient || !eq.sedeClient._id);
        if (equiposSinSede.length > 0) {
            Swal.fire({
                icon: 'error',
                title: 'Equipos sin sede',
                text: `Hay ${equiposSinSede.length} equipo(s) sin información de sede. Por favor, corrija los datos antes de crear la OT.`,
                confirmButtonText: 'Entendido'
            });
            return;
        }

        if (equipos.length > 500) {
            Swal.fire('Error', 'Se permiten máximo 500 equipos por orden de trabajo', 'error');
            return;
        }

        // Defensa en profundidad (spec items-adicionales-equipo-dependiente §3.3):
        // aunque los componentes de agregado ya deberían haber resuelto dependientes,
        // revalidamos antes de crear la OT para prevenir OTs incompletas.
        const resolverRes = await handleResolverDependientes(equipos.map(e => e._id));
        if (resolverRes.cancelado) {
            return;
        }
        let equiposFinal = equipos;
        let observacionFinal = observacion.trim();
        let omitirDependientes = false;
        if (resolverRes.incluirTodos && resolverRes.extra.length > 0) {
            equiposFinal = [...equipos, ...resolverRes.extra];
        }
        if (!resolverRes.incluirTodos && resolverRes.warning) {
            observacionFinal = observacionFinal
                ? `${observacionFinal}\n${resolverRes.warning}`
                : resolverRes.warning;
            omitirDependientes = true;
        }

        setLoading(prev => ({ ...prev, creacion: true }));

        try {
            const otData = {
                propuestaComercial: propuestaComercial ? propuestaComercial._id : null,
                numeroOrden: numeroOrden.trim(),
                cliente: cliente._id,
                sedeClient: sede._id,
                coordinador: userData._id, // Auto-assign coordinador from userData
                observacion: observacionFinal,
                ...(propuestaComercial && { propuestaComercial: propuestaComercial._id }),
                ...(omitirDependientes && { omitirDependientes: true }),
                equipos: equiposFinal.map(e => ({
                    equipo: e._id,
                    serie: e.serie,
                    marca: e.marca,
                    modelo: e.modelo,
                    item: e.item
                }))
            };

            console.log('🔵 Datos para crear OT:', otData);
            const response = await postDataOt(otData);

            Swal.fire('Éxito', 'OT creada correctamente', 'success');

            // Navigate to OT detail or list
            navigate(`/otlista/total`);
        } catch (error) {
            console.error('Error creating OT:', error);
            Swal.fire('Error', error.response?.data?.message || 'No se pudo crear la OT', 'error');
        } finally {
            setLoading(prev => ({ ...prev, creacion: false }));
        }
    };

    return {
        // State
        cliente,
        sede,
        equipos,
        numeroOrden,
        observacion,
        propuestaComercial,
        listaSedes,
        loading,

        // Modal states
        modalCrearCliente,
        modalEditarSede,
        modalCrearSede,
        modalDuplicados,
        modalResolverItems,
        modalDependientes,

        // Handlers
        setNumeroOrden,
        setObservacion,
        handleClienteChange,

        handlePropuestaChange: (propuesta) => {

            console.log('🔵 handlePropuestaChange llamado con:', propuesta);
            if (propuesta) {
                setPropuestaComercial(propuesta);
                setCliente(propuesta.cliente_id || null);
                setNumeroOrden(String(propuesta.consecutivo ?? ''));
            } else {
                setPropuestaComercial(null);
                setCliente(null);
                setNumeroOrden('');
            }
        },
        handleCrearCliente,
        handleSubmitCrearCliente,
        handleSedeChange,
        handleEditarSede,
        handleCrearSede,
        handleSubmitCrearSede,
        handleSubmitEditarSede,
        validarDuplicados,
        handleResolverItems,
        handleResolverDependientes,
        agregarEquipos,
        eliminarEquipo,
        editarEquipo,
        corregirEquipo,
        handleCrearOt,

        // Modal handlers
        closeModalCrearCliente: () => setModalCrearCliente({ show: false, data: null }),
        closeModalEditarSede: () => setModalEditarSede({ show: false, data: null }),
        closeModalCrearSede: () => setModalCrearSede({ show: false }),
        closeModalDuplicados: () => setModalDuplicados({ show: false, data: null }),
        closeModalResolverItems: () => setModalResolverItems({ show: false, data: null })
    };
};

export default useCrearOt;
